import { html, TemplateResult, CSSResultGroup } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import componentStyles from './idb-slider-video-youtube.css.lit';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { classMap } from 'lit/directives/class-map.js';
import { LightDomStyles } from '../../controllers/light-dom-styles';

export interface IdbSliderVideoYoutubeInterface extends HTMLElement {
  videoID: string;
}

/**
 * The IDB SliderVideoYoutube component
 *
 * @element idb-slider-video-youtube
 */
@customElement('idb-slider-video-youtube')
export class IdbSliderVideoYoutube
  extends OutlineElement
  implements IdbSliderVideoYoutubeInterface
{
  lightDomStyles = new LightDomStyles(this, componentStyles);
  static styles: CSSResultGroup = [componentStyles];

  @property({ type: String, reflect: true, attribute: 'id-rand' })
  idRand = `player-${Math.floor(Math.random() * 10000).toString()}`;

  @property({ type: String, attribute: 'video-id' })
  videoID: string;

  @property({ type: String, attribute: 'video-thumb' })
  videoThumb: string;

  @state()
  videoThumbState: boolean;

  @property({ type: Number, reflect: true, attribute: 'video-autoplay' })
  autoplay: number;

  @property({ type: Number, reflect: true, attribute: 'video-mute' })
  mute: number;

  @property({ type: Boolean, reflect: true, attribute: 'play-in-place' })
  playInPlace: boolean;

  @property({ type: String, reflect: true, attribute: 'video-url' })
  url: string;

  @property({ type: String, reflect: true, attribute: 'custom-class' })
  customClass = '';

  firstUpdated() {
    const id = this.idRand;
    const vid = this.videoID;
    const player = this.querySelector('#' + id) as HTMLElement;
    const iframe = document.createElement('iframe');
    if (player) {
      iframe.width = '480';
      iframe.height = '360';
      iframe.src = `https://www.youtube.com/embed/${vid}?autoplay=${
        this.autoplay
      }&mute=${this.autoplay || this.mute}&rel=0`;
      iframe.title = 'YouTube video player';
      iframe.allow =
        'accelerometer; clipboard-write; encrypted-media; picture-in-picture';
      iframe.allowFullscreen = true;

      player.innerHTML = '';
      player.appendChild(iframe);
    }
  }

  playIconHandler() {
    this.videoThumbState = !this.videoThumbState;
    const id = this.idRand;
    const player = this.querySelector('#' + id) as HTMLElement;
    const iframe = player.querySelector('iframe');
    if (iframe && iframe.contentWindow) {
      const src = iframe.src;
      iframe.src = src.replace('autoplay=0', 'autoplay=1');
      iframe.allow += ';autoplay';
      player.innerHTML = '';
      player.appendChild(iframe);
    }
  }

  render(): TemplateResult {
    return html`
      <div class="${this.customClass}">
          ${
            this.videoThumb
              ? html`
            ${this.imageTemplate()}`
              : null
          }
        ${
          this.playInPlace
            ? html`<div id="${this.idRand}" class="player"></div>`
            : null
        }
      </div>`;
  }

  createRenderRoot() {
    return this;
  }

  /*
   * Template partial for the playInPlace content.
   * @param classes
   * @returns TemplateResult | null
   */
  imageTemplate(): TemplateResult | null {
    const videoThumbClasses = {
      'video-thumb': true,
      'hide': this.videoThumbState,
    };

    const videoPlayClasses = {
      'video-play': true,
      'hide': this.videoThumbState,
    };

    const videoOverlay = {
      'video-overlay': true,
      'hide': this.videoThumbState,
    };

    const content = html`<div class="${classMap(videoThumbClasses)}">
      <img src="${this.videoThumb}" />
    </div>
    <div class="${classMap(videoOverlay)}"></div>
    <div class="${classMap(videoPlayClasses)}">
      <button @click="${this.playInPlace ? this.playIconHandler : null}">
        <idb-icon icon="play-circle" stroke-width="2"></idb-icon>
      </button>
    </div>`;

    if (this.playInPlace) return html`${content}`;
    else {
      return html`
      <a href="${this.url}" target="_blank">${content}</a>`;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'idb-slider-video-youtube': IdbSliderVideoYoutube;
  }
}
